<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import DealProductsAPI from 'dashboard/api/pipelines/dealProducts';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import AddAttribute from 'dashboard/routes/dashboard/settings/attributes/AddAttribute.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  deal: { type: Object, required: true },
});

const emit = defineEmits(['click', 'edit', 'openConversation', 'openContact']);

const PRIORITY_OPTIONS = [
  {
    value: 'urgent',
    icon: 'i-lucide-chevrons-up',
    color: '#dc2626',
    label: 'Urgente',
  },
  {
    value: 'high',
    icon: 'i-lucide-chevron-up',
    color: '#eab308',
    label: 'Alta',
  },
  { value: 'medium', icon: 'i-lucide-equal', color: '#3b82f6', label: 'Media' },
  {
    value: 'low',
    icon: 'i-lucide-chevron-down',
    color: '#64748b',
    label: 'Baixa',
  },
  {
    value: 'none',
    icon: 'i-lucide-minus',
    color: '#64748b',
    label: 'Sem prioridade',
  },
];
const store = useStore();

const actionsButtonRef = ref(null);
const showActionsMenu = ref(false);
const showPriorityMenu = ref(false);
const showAssigneeMenu = ref(false);
const showLabelsMenu = ref(false);
const showProductsMenu = ref(false);
const dealProductsView = ref('summary');
const dealProductSearch = ref('');
const showCreateDealProductForm = ref(false);
const dealNewProduct = ref({ name: '', sku: '', price: '' });
const dealPopoverSaving = ref(false);
const showLoseDealModal = ref(false);
const confirmDeleteModal = ref(null);
const showCustomAttributesModal = ref(false);
const showCustomAttributesManageModal = ref(false);
const showAddDealAttributeModal = ref(false);
const selectedLossReasonId = ref(null);
const lossJustification = ref('');
const assigneeSearch = ref('');
const labelSearch = ref('');
const selectedAssigneeIds = ref([]);
const selectedLabels = ref([]);
const customAttributesDraft = ref({});
const dealCustomFieldKeys = ref([]);
const selectedCustomAttributeKey = ref(null);
const dealSelectedProducts = ref([]);
const dealCouponCode = ref('');
const dealSelectedCoupon = ref(null);
const dealManualDiscount = ref('0');
const dealSurcharge = ref('0');
const dealShipping = ref('0');
const actionsMenuStyle = ref({ top: '120px', left: '24px' });
const priorityMenuStyle = ref({ top: '120px', left: '24px' });
const assigneeMenuStyle = ref({ top: '120px', left: '24px' });
const labelsMenuStyle = ref({ top: '120px', left: '24px' });
const productsMenuStyle = ref({ top: '120px', left: '24px' });

const accountId = computed(() => store.getters.getCurrentAccountId);
const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);
const agents = computed(() => store.getters['agents/getAgents'] || []);
const labels = computed(() => store.getters['labels/getLabels'] || []);
const products = computed(() => store.getters['products/getProducts'] || []);
const coupons = computed(() => store.getters['coupons/getCoupons'] || []);
const lossReasons = computed(
  () => store.getters['dealLossReasons/getLossReasons'] || []
);
const dealAttributeDefinitions = computed(() => {
  const getter = store.getters['attributes/getAttributesByModel'];
  if (typeof getter !== 'function') return [];
  return getter('deal_attribute') || [];
});
const pipelineById = computed(() =>
  Object.fromEntries(
    pipelines.value.map(pipeline => [Number(pipeline.id), pipeline])
  )
);
const getConversationById = computed(() => store.getters.getConversationById);
const getInboxById = computed(() => store.getters['inboxes/getInbox']);

const dealAssignees = computed(() => {
  const customIds = Array.isArray(
    props.deal?.custom_attributes?.responsible_agent_ids
  )
    ? props.deal.custom_attributes.responsible_agent_ids.map(id => Number(id))
    : [];
  const nativeId = Number(props.deal?.assignee?.id || props.deal?.assignee_id);
  const ids = [
    ...new Set([...customIds, nativeId].filter(id => !Number.isNaN(id))),
  ];
  if (!ids.length && props.deal?.assignee) return [props.deal.assignee];
  return agents.value.filter(agent => ids.includes(Number(agent.id)));
});

const getPipelineName = computed(() => {
  const pipeline = pipelines.value.find(
    item => Number(item.id) === Number(props.deal.pipeline_id)
  );
  return pipeline?.name || 'Sem funil';
});
const dealContactName = computed(() => {
  return props.deal?.contact?.name || 'Contato';
});
const dealContactAvatar = computed(() => {
  return (
    props.deal?.contact?.thumbnail || props.deal?.contact?.avatar_url || null
  );
});
const routeConversationId = computed(() =>
  Number(
    props.deal?.conversation_display_id || props.deal?.conversation_id || 0
  )
);
const dealConversation = computed(() => {
  const conversationId = routeConversationId.value;
  if (!conversationId) return null;
  const getter = getConversationById.value;
  if (typeof getter !== 'function') return null;
  return getter(conversationId) || null;
});
const dealInbox = computed(() => {
  const inboxId =
    Number(props.deal?.conversation_inbox_id || 0) ||
    Number(dealConversation.value?.inbox_id || 0);
  if (!inboxId) return null;
  const getter = getInboxById.value;
  if (typeof getter !== 'function') return null;
  return getter(inboxId) || null;
});
const resolveChannelIcon = (channelType, medium, provider) => {
  const channelTypeIconMap = {
    'Channel::Api': 'i-woot-api',
    'Channel::Email': 'i-woot-mail',
    'Channel::FacebookPage': 'i-woot-messenger',
    'Channel::Line': 'i-woot-line',
    'Channel::Sms': 'i-woot-sms',
    'Channel::Telegram': 'i-woot-telegram',
    'Channel::TwilioSms': 'i-woot-sms',
    'Channel::TwitterProfile': 'i-ri-twitter-x-fill',
    'Channel::WebWidget': 'i-woot-website',
    'Channel::Whatsapp': 'i-woot-whatsapp',
    'Channel::Instagram': 'i-woot-instagram',
    'Channel::Tiktok': 'i-woot-tiktok',
    'Channel::Voice': 'i-ri-phone-fill',
  };
  const providerIconMap = {
    microsoft: 'i-woot-outlook',
    google: 'i-woot-gmail',
  };

  let icon = channelTypeIconMap[channelType];
  if (
    channelType === 'Channel::Email' &&
    provider &&
    providerIconMap[provider]
  ) {
    icon = providerIconMap[provider];
  }
  if (channelType === 'Channel::TwilioSms' && medium === 'whatsapp') {
    icon = 'i-woot-whatsapp';
  }
  return icon || null;
};
const dealChannelIcon = computed(() => {
  const channelType =
    props.deal?.conversation_channel_type || dealInbox.value?.channel_type;
  const medium =
    props.deal?.conversation_channel_medium || dealInbox.value?.medium;
  const provider =
    props.deal?.conversation_channel_provider || dealInbox.value?.provider;
  return resolveChannelIcon(channelType, medium, provider);
});
const dealChannelTitle = computed(() => {
  return dealInbox.value?.name || 'Canal da conversa';
});
const stageList = computed(() => {
  const stages =
    pipelineById.value[Number(props.deal.pipeline_id)]?.stages || [];
  return [...stages].sort((a, b) => (a.position || 0) - (b.position || 0));
});
const dealIsClosed = computed(() =>
  ['won', 'lost'].includes(props.deal?.status)
);
const getDaysSince = (value, endValue = null) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '0min';
  const endDate = endValue ? new Date(endValue) : new Date();
  const endTime = Number.isNaN(endDate.getTime())
    ? Date.now()
    : endDate.getTime();
  const diff = Math.max(0, endTime - date.getTime());
  const totalMinutes = Math.floor(diff / (1000 * 60));
  if (totalMinutes < 60) return `${Math.max(0, totalMinutes)}min`;
  const totalHours = Math.floor(diff / (1000 * 60 * 60));
  if (totalHours < 24) return `${Math.max(1, totalHours)}h`;
  const totalDays = Math.floor(diff / (1000 * 60 * 60 * 24));
  return `${Math.max(1, totalDays)}d`;
};
const openDaysLabel = computed(() =>
  getDaysSince(
    props.deal.created_at,
    dealIsClosed.value ? props.deal.updated_at : null
  )
);
const stageDaysLabel = computed(() =>
  getDaysSince(
    props.deal.updated_at || props.deal.created_at,
    dealIsClosed.value ? props.deal.updated_at : null
  )
);

const priorityMeta = computed(() => {
  const priority =
    props.deal?.priority || props.deal?.custom_attributes?.priority || 'none';
  return (
    PRIORITY_OPTIONS.find(item => item.value === priority) ||
    PRIORITY_OPTIONS[4]
  );
});

const MAX_VISIBLE_LABELS = 6;
// eslint-disable-next-line no-use-before-define
const allLabels = computed(() => getDealLabels());
const visibleLabels = computed(() =>
  allLabels.value.slice(0, MAX_VISIBLE_LABELS)
);
const hiddenLabelsCount = computed(() =>
  Math.max(0, allLabels.value.length - MAX_VISIBLE_LABELS)
);
const getDealTitle = computed(() => {
  const normalizedTitle = (props.deal?.title || '').trim();
  return (
    normalizedTitle || `Negocio #${props.deal.display_id || props.deal.id}`
  );
});
const dealDisplayAmount = computed(() => {
  const amount = Number(props.deal?.amount);
  if (Number.isFinite(amount) && amount > 0) return amount;

  const calculated = Number(props.deal?.custom_attributes?.calculated_total);
  if (Number.isFinite(calculated) && calculated > 0) return calculated;

  const fromProducts = (props.deal?.products || []).reduce((sum, item) => {
    const quantity = Number(item?.quantity || 0);
    const unitPrice = Number(item?.unit_price || 0);
    if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) return sum;
    return sum + quantity * unitPrice;
  }, 0);
  return fromProducts > 0 ? fromProducts : 0;
});
const getProductSummary = computed(() => {
  const dealProducts = props.deal?.products || [];
  if (!dealProducts.length) return 'Sem produtos';
  return `${dealProducts.length} produto(s)`;
});
const RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS = new Set([
  'subtotal_products',
  'coupon_code',
  'coupon_discount',
  'manual_discount',
  'surcharge',
  'shipping',
  'calculated_total',
  'priority',
  'responsible_agent_ids',
]);
const isCustomFieldValuePresent = value => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};
const dealCustomFieldsCount = computed(() => {
  const attrs = props.deal?.custom_attributes || {};
  const definitionKeys = new Set(
    dealAttributeDefinitions.value.map(item => item.attribute_key)
  );
  return Object.entries(attrs).filter(([key, value]) => {
    if (RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS.has(key)) return false;
    if (!definitionKeys.has(key)) return false;
    return isCustomFieldValuePresent(value);
  }).length;
});
const dealCustomFieldsEntries = computed(() => {
  const attrs = props.deal?.custom_attributes || {};
  const definitionsByKey = new Map(
    dealAttributeDefinitions.value.map(item => [item.attribute_key, item])
  );
  return Object.entries(attrs)
    .filter(([key, value]) => {
      if (RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS.has(key)) return false;
      if (!definitionsByKey.has(key)) return false;
      return isCustomFieldValuePresent(value);
    })
    .map(([key, value]) => ({
      key,
      label: definitionsByKey.get(key)?.attribute_display_name || key,
      value,
    }));
});
const showOnCardAttributes = computed(() => {
  const attrs = props.deal?.custom_attributes || {};
  return dealAttributeDefinitions.value
    .filter(def => def.show_on_card)
    .map(def => ({
      key: def.attribute_key,
      label: def.attribute_display_name,
      value: attrs[def.attribute_key] ?? null,
    }));
});
const dealAttributeDefinitionOptions = computed(() => {
  const used = new Set(dealCustomFieldKeys.value);
  return dealAttributeDefinitions.value
    .filter(attribute => !used.has(attribute.attribute_key))
    .map(attribute => ({
      label: attribute.attribute_display_name,
      value: attribute.attribute_key,
    }));
});
const activeProducts = computed(() =>
  products.value.filter(product => product.active !== false)
);
const filteredDealCatalogProducts = computed(() => {
  const term = dealProductSearch.value.trim().toLowerCase();
  const selectedIds = new Set(
    dealSelectedProducts.value.map(item => Number(item.product_id))
  );
  const base = activeProducts.value.filter(
    product => !selectedIds.has(Number(product.id))
  );
  if (!term) return base;
  return base.filter(product =>
    [product.name, product.default_variant?.sku]
      .filter(Boolean)
      .some(value => String(value).toLowerCase().includes(term))
  );
});

const filteredAgents = computed(() => {
  const term = assigneeSearch.value.trim().toLowerCase();
  if (!term) return agents.value;
  return agents.value.filter(agent => agent.name?.toLowerCase().includes(term));
});

const filteredLabels = computed(() => {
  const term = labelSearch.value.trim().toLowerCase();
  if (!term) return labels.value;
  return labels.value.filter(label =>
    label.title?.toLowerCase().includes(term)
  );
});

const resetMenus = () => {
  showActionsMenu.value = false;
  showPriorityMenu.value = false;
  showAssigneeMenu.value = false;
  showLabelsMenu.value = false;
  showProductsMenu.value = false;
};

const getPopoverPosition = (event, options = {}) => {
  const {
    width = 320,
    preferredHeight = 360,
    align = 'end',
    offset = 8,
  } = options;
  const rect = event?.currentTarget?.getBoundingClientRect?.();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  if (!rect) return { top: '120px', left: '24px' };
  let top = rect.bottom + offset;
  let left = align === 'start' ? rect.left : rect.right - width;
  if (left + width > viewportWidth - 8) left = viewportWidth - width - 8;
  if (left < 8) left = 8;
  if (top + preferredHeight > viewportHeight - 8) {
    top = rect.top - preferredHeight - offset;
  }
  if (top < 8) top = 8;
  return { top: `${Math.round(top)}px`, left: `${Math.round(left)}px` };
};

const closeOverlaysOnOutsideClick = event => {
  const inside = event.target?.closest?.('.deal-popover');
  if (!inside) resetMenus();
};

const toggleActionsMenu = event => {
  const next = !showActionsMenu.value;
  resetMenus();
  if (next) {
    actionsMenuStyle.value = getPopoverPosition(event, {
      width: 224,
      preferredHeight: 260,
      align: 'end',
    });
  }
  showActionsMenu.value = next;
};

const openActionsMenuFromContextMenu = () => {
  const next = !showActionsMenu.value;
  resetMenus();
  if (next) {
    actionsMenuStyle.value = getPopoverPosition(
      { currentTarget: actionsButtonRef.value },
      { width: 224, preferredHeight: 260, align: 'end' }
    );
    showActionsMenu.value = true;
  }
};

const togglePriorityMenu = event => {
  const next = !showPriorityMenu.value;
  resetMenus();
  if (next) {
    priorityMenuStyle.value = getPopoverPosition(event, {
      width: 230,
      preferredHeight: 300,
      align: 'end',
    });
  }
  showPriorityMenu.value = next;
};

const toggleAssigneeMenu = event => {
  const next = !showAssigneeMenu.value;
  resetMenus();
  selectedAssigneeIds.value = dealAssignees.value.map(item => Number(item.id));
  if (next) {
    assigneeMenuStyle.value = getPopoverPosition(event, {
      width: 320,
      preferredHeight: 420,
      align: 'end',
    });
  }
  showAssigneeMenu.value = next;
};

const toggleLabelsMenu = event => {
  const next = !showLabelsMenu.value;
  resetMenus();
  // eslint-disable-next-line no-use-before-define
  selectedLabels.value = [...getDealLabels()];
  if (next) {
    labelsMenuStyle.value = getPopoverPosition(event, {
      width: 300,
      preferredHeight: 360,
      align: 'end',
    });
  }
  showLabelsMenu.value = next;
};

const toggleProductsMenu = event => {
  const next = !showProductsMenu.value;
  resetMenus();
  if (next) {
    // eslint-disable-next-line no-use-before-define
    initializeDealProductsState();
    dealProductsView.value = 'summary';
    dealProductSearch.value = '';
    showCreateDealProductForm.value = false;
    dealNewProduct.value = { name: '', sku: '', price: '' };
    productsMenuStyle.value = getPopoverPosition(event, {
      width: 420,
      preferredHeight: 520,
      align: 'start',
    });
  }
  showProductsMenu.value = next;
};

const getDealLabels = () => {
  const labelList = props.deal?.label_list || props.deal?.labels;
  if (Array.isArray(labelList)) return labelList;
  if (typeof labelList === 'string') {
    return labelList
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};
const getLabelColorByTitle = title => {
  const label = labels.value.find(item => item.title === title);
  return label?.color || '#64748B';
};

const setPriority = async priority => {
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: props.deal.id,
      deal: {
        priority: priority === 'none' ? null : priority,
        custom_attributes: {
          ...(props.deal.custom_attributes || {}),
          priority: priority === 'none' ? null : priority,
        },
      },
    });
    resetMenus();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || 'Erro ao atualizar prioridade'
    );
  }
};

const setDealStage = async stageId => {
  if (!stageId || Number(stageId) === Number(props.deal.pipeline_stage_id))
    return;
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: props.deal.id,
      deal: {
        pipeline_stage_id: Number(stageId),
      },
    });
  } catch (error) {
    useAlert(error?.response?.data?.errors?.[0] || 'Erro ao atualizar estagio');
  }
};

const getStageProgressStyle = (stage, index) => {
  const currentIndex = stageList.value.findIndex(
    item => Number(item.id) === Number(props.deal.pipeline_stage_id)
  );
  if (currentIndex === -1) return { backgroundColor: '#E2E8F0' };
  if (index <= currentIndex) {
    return { backgroundColor: stage.color || '#6B7280' };
  }
  return { backgroundColor: '#E2E8F0' };
};

const getStageTooltip = stage => {
  const isCurrent = Number(stage.id) === Number(props.deal.pipeline_stage_id);
  return isCurrent ? `${stage.name} (atual)` : stage.name;
};

const toggleAssignee = agent => {
  const id = Number(agent.id);
  if (selectedAssigneeIds.value.includes(id)) {
    selectedAssigneeIds.value = selectedAssigneeIds.value.filter(
      item => item !== id
    );
    return;
  }
  selectedAssigneeIds.value = [...selectedAssigneeIds.value, id];
};

const saveAssignees = async () => {
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: props.deal.id,
      deal: {
        assignee_id: selectedAssigneeIds.value[0] || null,
        custom_attributes: {
          ...(props.deal.custom_attributes || {}),
          responsible_agent_ids: selectedAssigneeIds.value,
        },
      },
    });
    resetMenus();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || 'Erro ao atualizar responsaveis'
    );
  }
};

const toggleLabel = title => {
  if (selectedLabels.value.includes(title)) {
    selectedLabels.value = selectedLabels.value.filter(item => item !== title);
    return;
  }
  selectedLabels.value = [...selectedLabels.value, title];
};

const saveLabels = async () => {
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: props.deal.id,
      deal: {
        label_list: selectedLabels.value,
      },
    });
    resetMenus();
  } catch (error) {
    useAlert(error?.response?.data?.errors?.[0] || 'Erro ao atualizar labels');
  }
};

const initializeDealProductsState = () => {
  const attrs = props.deal?.custom_attributes || {};
  const existingProducts = props.deal?.products || [];
  dealSelectedProducts.value = existingProducts.map(item => ({
    product_id: Number(item.product_id || item.id),
    deal_product_id: Number(item.deal_product_id || item.id),
    name: item.name,
    sku: item.sku || item.product?.sku || '',
    quantity: Number(item.quantity || 1),
    unit_price: Number(item.unit_price || item.price || 0),
  }));
  dealCouponCode.value = attrs.coupon_code || '';
  dealManualDiscount.value = String(attrs.manual_discount || 0);
  dealSurcharge.value = String(attrs.surcharge || 0);
  dealShipping.value = String(attrs.shipping || 0);

  if (dealCouponCode.value) {
    const normalized = dealCouponCode.value.trim().toUpperCase();
    dealSelectedCoupon.value =
      coupons.value.find(
        coupon =>
          coupon.code?.toUpperCase() === normalized && coupon.active !== false
      ) || null;
  } else {
    dealSelectedCoupon.value = null;
  }
};

const getParsedNumber = value => {
  const parsed = Number(value || 0);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const dealSubtotalProducts = computed(() =>
  dealSelectedProducts.value.reduce(
    (sum, item) =>
      sum + getParsedNumber(item.quantity) * getParsedNumber(item.unit_price),
    0
  )
);

const dealCouponDiscount = computed(() => {
  if (!dealSelectedCoupon.value) return 0;
  const coupon = dealSelectedCoupon.value;
  if (coupon.discount_type === 'percentage') {
    return (dealSubtotalProducts.value * Number(coupon.value || 0)) / 100;
  }
  return Number(coupon.value || 0);
});

const dealFinalAmount = computed(() => {
  const total =
    dealSubtotalProducts.value -
    dealCouponDiscount.value -
    getParsedNumber(dealManualDiscount.value) +
    getParsedNumber(dealSurcharge.value) +
    getParsedNumber(dealShipping.value);
  return Math.max(total, 0);
});

const addProductToDeal = productId => {
  const parsedId = Number(productId);
  if (!parsedId) return;
  const existing = dealSelectedProducts.value.find(
    item => Number(item.product_id) === parsedId
  );
  if (existing) {
    existing.quantity = getParsedNumber(existing.quantity) + 1;
    return;
  }
  const product = activeProducts.value.find(
    item => Number(item.id) === parsedId
  );
  if (!product) return;
  dealSelectedProducts.value.push({
    product_id: Number(product.id),
    deal_product_id: null,
    name: product.name,
    sku: product.default_variant?.sku || '',
    quantity: 1,
    unit_price: Number(product.default_variant?.price || 0),
  });
};

const addProductToDealPopover = product => {
  if (!product?.id) return;
  addProductToDeal(product.id);
  dealProductsView.value = 'summary';
};

const increaseDealQty = item => {
  item.quantity = getParsedNumber(item.quantity) + 1;
};

const decreaseDealQty = item => {
  const quantity = getParsedNumber(item.quantity);
  if (quantity <= 1) {
    // eslint-disable-next-line no-use-before-define
    removeDealProduct(item.product_id);
    return;
  }
  item.quantity = quantity - 1;
};

const removeDealProduct = productId => {
  dealSelectedProducts.value = dealSelectedProducts.value.filter(
    item => Number(item.product_id) !== Number(productId)
  );
};

const applyDealCoupon = () => {
  const normalized = dealCouponCode.value.trim().toUpperCase();
  if (!normalized) {
    dealSelectedCoupon.value = null;
    return;
  }
  const coupon = coupons.value.find(
    item => item.code?.toUpperCase() === normalized && item.active !== false
  );
  if (!coupon) {
    useAlert('Cupom nao encontrado ou inativo');
    return;
  }
  dealSelectedCoupon.value = coupon;
  dealCouponCode.value = coupon.code;
};

const createDealProductQuickly = async () => {
  if (!dealNewProduct.value.name || !dealNewProduct.value.sku) {
    useAlert('Preencha nome e SKU do produto');
    return;
  }

  try {
    await store.dispatch('products/create', {
      accountId: accountId.value,
      product: {
        name: dealNewProduct.value.name,
        sku: dealNewProduct.value.sku,
        price: Number(dealNewProduct.value.price || 0),
        active: true,
      },
    });
    await store.dispatch('products/get', { accountId: accountId.value });
    showCreateDealProductForm.value = false;
    dealNewProduct.value = { name: '', sku: '', price: '' };
  } catch (error) {
    useAlert(error?.response?.data?.errors?.[0] || 'Erro ao criar produto');
  }
};

const syncDealProducts = async ({
  dealId,
  existingProducts,
  selectedItems,
}) => {
  const existingMap = new Map();
  existingProducts.forEach(item => {
    existingMap.set(Number(item.product_id || item.id), item);
  });

  const selectedIds = selectedItems.map(item => Number(item.product_id));
  const toDelete = existingProducts.filter(
    item => !selectedIds.includes(Number(item.product_id || item.id))
  );
  const toCreate = selectedItems.filter(
    item => !existingMap.has(Number(item.product_id))
  );
  const toUpdate = selectedItems.filter(item =>
    existingMap.has(Number(item.product_id))
  );

  await Promise.all(
    toDelete.map(item => {
      const relationId = item.deal_product_id || item.id;
      if (!relationId) return Promise.resolve();
      return DealProductsAPI.delete(accountId.value, dealId, relationId);
    })
  );

  await Promise.all(
    toUpdate.map(item => {
      const existing = existingMap.get(Number(item.product_id));
      const relationId = existing?.deal_product_id || existing?.id;
      if (!relationId) return Promise.resolve();
      return DealProductsAPI.update(accountId.value, dealId, relationId, {
        quantity: getParsedNumber(item.quantity),
        unit_price: getParsedNumber(item.unit_price),
      });
    })
  );

  await Promise.all(
    toCreate.map(item =>
      DealProductsAPI.create(accountId.value, dealId, {
        product_id: Number(item.product_id),
        quantity: getParsedNumber(item.quantity),
        unit_price: getParsedNumber(item.unit_price),
      })
    )
  );
};

const saveDealProducts = async () => {
  try {
    dealPopoverSaving.value = true;
    await syncDealProducts({
      dealId: props.deal.id,
      existingProducts: props.deal.products || [],
      selectedItems: dealSelectedProducts.value,
    });

    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: props.deal.id,
      deal: {
        amount: dealFinalAmount.value,
        custom_attributes: {
          ...(props.deal.custom_attributes || {}),
          subtotal_products: dealSubtotalProducts.value,
          coupon_code: dealSelectedCoupon.value?.code || null,
          coupon_discount: dealCouponDiscount.value,
          manual_discount: getParsedNumber(dealManualDiscount.value),
          surcharge: getParsedNumber(dealSurcharge.value),
          shipping: getParsedNumber(dealShipping.value),
          calculated_total: dealFinalAmount.value,
        },
      },
    });

    showProductsMenu.value = false;
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || 'Erro ao salvar produtos do negocio'
    );
  } finally {
    dealPopoverSaving.value = false;
  }
};

const emitEdit = () => {
  resetMenus();
  emit('edit', props.deal);
};

const isWonOrLost = computed(() =>
  ['won', 'lost'].includes(String(props.deal?.status || '').toLowerCase())
);

const restoreDealStatus = async () => {
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: props.deal.id,
      deal: {
        status: 'open',
        deal_loss_reason_id: null,
        loss_justification: null,
      },
    });
    resetMenus();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] ||
        'Erro ao restaurar status do negocio'
    );
  }
};

const markWon = async () => {
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: props.deal.id,
      deal: { status: 'won' },
    });
    resetMenus();
  } catch (error) {
    useAlert(error?.response?.data?.errors?.[0] || 'Erro ao ganhar negocio');
  }
};

const openLoseModal = () => {
  resetMenus();
  selectedLossReasonId.value = null;
  lossJustification.value = '';
  showLoseDealModal.value = true;
};

const closeLoseModal = () => {
  showLoseDealModal.value = false;
  selectedLossReasonId.value = null;
  lossJustification.value = '';
};

const openDeleteModal = () => {
  resetMenus();
  confirmDeleteModal.value?.open();
};

const confirmLoseDeal = async () => {
  if (!selectedLossReasonId.value) {
    useAlert('Selecione um motivo da perda');
    return;
  }
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: props.deal.id,
      deal: {
        status: 'lost',
        deal_loss_reason_id: selectedLossReasonId.value,
        loss_justification: lossJustification.value?.trim() || null,
      },
    });
    closeLoseModal();
  } catch (error) {
    useAlert(error?.response?.data?.errors?.[0] || 'Erro ao perder negocio');
  }
};

const removeDeal = async () => {
  try {
    await store.dispatch('deals/delete', {
      accountId: accountId.value,
      dealId: props.deal.id,
    });
    resetMenus();
  } catch (error) {
    useAlert(error?.response?.data?.errors?.[0] || 'Erro ao excluir negocio');
  }
};

const openCustomAttributesModal = () => {
  showCustomAttributesModal.value = true;
};

const initializeCustomAttributesDraft = () => {
  const attrs = props.deal?.custom_attributes || {};
  const definitionKeys = new Set(
    dealAttributeDefinitions.value.map(item => item.attribute_key)
  );
  const keys = Object.keys(attrs).filter(key => {
    if (RESERVED_DEAL_CUSTOM_ATTRIBUTE_KEYS.has(key)) return false;
    return definitionKeys.has(key);
  });
  dealCustomFieldKeys.value = keys;
  customAttributesDraft.value = keys.reduce((acc, key) => {
    acc[key] = attrs[key];
    return acc;
  }, {});
  selectedCustomAttributeKey.value = null;
};

const closeCustomAttributesModal = () => {
  showCustomAttributesModal.value = false;
};

const openCustomAttributesManagementModal = () => {
  initializeCustomAttributesDraft();
  showCustomAttributesModal.value = false;
  showCustomAttributesManageModal.value = true;
};

const closeCustomAttributesManagementModal = () => {
  showCustomAttributesManageModal.value = false;
  selectedCustomAttributeKey.value = null;
};

const addCustomFieldToDraft = () => {
  if (!selectedCustomAttributeKey.value) return;
  if (dealCustomFieldKeys.value.includes(selectedCustomAttributeKey.value)) {
    selectedCustomAttributeKey.value = null;
    return;
  }
  dealCustomFieldKeys.value.push(selectedCustomAttributeKey.value);
  customAttributesDraft.value = {
    ...customAttributesDraft.value,
    [selectedCustomAttributeKey.value]: '',
  };
  selectedCustomAttributeKey.value = null;
};

const removeCustomFieldFromDraft = key => {
  dealCustomFieldKeys.value = dealCustomFieldKeys.value.filter(
    item => item !== key
  );
  const next = { ...customAttributesDraft.value };
  delete next[key];
  customAttributesDraft.value = next;
};

const getAttributeDefinition = key => {
  return (
    dealAttributeDefinitions.value.find(item => item.attribute_key === key) ||
    null
  );
};

const getDraftCustomFieldLabel = key => {
  return getAttributeDefinition(key)?.attribute_display_name || key;
};

const getDraftCustomFieldType = key => {
  return getAttributeDefinition(key)?.attribute_display_type || 'text';
};

const getDraftCustomFieldOptions = key => {
  return getAttributeDefinition(key)?.attribute_values || [];
};

const getDraftCustomFieldComboOptions = key => {
  return getDraftCustomFieldOptions(key).map(value => ({
    label: value,
    value,
  }));
};

const openAddDealAttributeModal = () => {
  showAddDealAttributeModal.value = true;
};

const closeAddDealAttributeModal = async () => {
  showAddDealAttributeModal.value = false;
  await store.dispatch('attributes/get').catch(() => null);
};

const isSelectField = field =>
  ['list', 'text_list', 'select'].includes(field.attribute_display_type);

const getDraftCustomFieldValue = key => {
  const value = customAttributesDraft.value[key];
  return value === null || value === undefined ? '' : String(value);
};

const saveCustomAttributes = async () => {
  try {
    await store.dispatch('deals/update', {
      accountId: accountId.value,
      dealId: props.deal.id,
      deal: {
        custom_attributes: {
          ...(props.deal.custom_attributes || {}),
          ...customAttributesDraft.value,
        },
      },
    });
    closeCustomAttributesManagementModal();
    closeCustomAttributesModal();
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.[0] || 'Erro ao atualizar atributos'
    );
  }
};

const openConversation = () => {
  if (!routeConversationId.value) return;
  emit('openConversation', routeConversationId.value);
};

const formatCurrency = (amount, currency = 'BRL') => {
  const numericAmount = Number(amount || 0);
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
      minimumFractionDigits: 2,
    }).format(numericAmount);
  } catch {
    return `R$ ${numericAmount.toFixed(2)}`;
  }
};

const formatDate = dateString => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
};

const formatDealCreatedAt = value => {
  if (!value) return '-';
  const createdAt = new Date(value);
  if (Number.isNaN(createdAt.getTime())) return '-';
  const diffMs = Date.now() - createdAt.getTime();
  if (diffMs >= 0 && diffMs < 24 * 60 * 60 * 1000) {
    if (diffMs < 60 * 60 * 1000) {
      return `${Math.max(1, Math.floor(diffMs / (60 * 1000)))}min`;
    }
    return `${Math.max(1, Math.floor(diffMs / (60 * 60 * 1000)))}h`;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
  }).format(createdAt);
};

watch(
  () => props.deal,
  () => {
    selectedLabels.value = getDealLabels();
  },
  { immediate: true, deep: true }
);

document.addEventListener('mousedown', closeOverlaysOnOutsideClick);
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', closeOverlaysOnOutsideClick);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div
    class="rounded-xl border border-n-weak bg-n-solid-1 px-3 py-3 relative transition-all hover:border-n-brand hover:shadow-md select-none"
    @contextmenu.prevent="openActionsMenuFromContextMenu"
  >
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0 flex-1 space-y-1.5">
        <p class="truncate text-sm font-semibold text-n-slate-12">
          <span class="inline-flex items-center gap-1.5">
            <span
              v-if="deal.status === 'won'"
              class="i-lucide-circle-check size-4 text-green-600"
              title="Ganho"
            />
            <span
              v-else-if="deal.status === 'lost'"
              class="i-lucide-circle-x size-4 text-red-600"
              title="Perdido"
            />
            <button
              class="text-left hover:underline"
              type="button"
              @click.stop="$emit('click', deal)"
            >
              {{ getDealTitle }}
            </button>
          </span>
        </p>
        <div class="flex items-center gap-2 text-xs text-n-slate-11">
          <button
            class="relative shrink-0 rounded-full hover:ring-2 hover:ring-n-brand/50 transition-all"
            :title="`Ver contato: ${dealContactName}`"
            @click.stop="emit('openContact', { contactId: deal.contact?.id, excludeConversationId: deal.conversation_display_id || deal.conversation_id || null })"
          >
            <Avatar
              :name="dealContactName"
              :src="dealContactAvatar"
              :size="20"
              rounded-full
            />
            <span
              v-if="dealChannelIcon"
              class="absolute -bottom-0.5 -right-0.5 inline-flex items-center justify-center size-3.5 rounded-full bg-n-solid-1 border border-n-weak text-[9px] text-n-slate-12"
              :title="dealChannelTitle"
            >
              <span class="size-2.5" :class="dealChannelIcon" />
            </span>
          </button>
          <span class="truncate" :title="dealContactName">{{
            dealContactName
          }}</span>
        </div>
        <div class="mt-1 flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <button
              class="text-xs text-n-brand hover:underline inline-flex items-center gap-1"
              :title="`Produtos: ${getProductSummary}`"
              @click.stop="toggleProductsMenu($event)"
            >
              <span class="i-lucide-package size-3.5" />
              {{ getProductSummary }}
            </button>
            <button
              v-if="routeConversationId"
              class="text-xs text-n-brand hover:underline inline-flex items-center gap-1"
              :title="`Abrir conversa #${routeConversationId}`"
              @click.stop="openConversation"
            >
              <span class="i-lucide-messages-square size-3.5" />
              Conversa
            </button>
          </div>
          <div class="flex items-center gap-2">
            <span
              class="inline-flex items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-1 text-[11px] text-n-slate-11"
              :title="dealIsClosed ? 'Tempo total em aberto (negocio fechado)' : 'Tempo em aberto'"
            >
              <span class="i-lucide-clock-3 size-3.5" />
              {{ openDaysLabel }} aberto
            </span>
            <span
              class="inline-flex items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-1 text-[11px] text-n-slate-11"
              :title="dealIsClosed ? 'Tempo total no estagio (negocio fechado)' : 'Tempo no estagio'"
            >
              <span class="i-lucide-timer size-3.5" />
              {{ stageDaysLabel }} estagio
            </span>
          </div>
        </div>
        <div class="flex items-center gap-2 text-xs text-n-slate-11">
          <span class="i-lucide-circle-dollar-sign size-4 text-n-slate-10" />
          <span>{{ formatCurrency(dealDisplayAmount, deal.currency) }}</span>
        </div>
      </div>

      <div class="shrink-0 flex items-start gap-1">
        <button
          ref="actionsButtonRef"
          class="p-1.5 rounded hover:bg-n-alpha-2"
          title="Mais opcoes"
          @click.stop="toggleActionsMenu($event)"
        >
          <span class="i-lucide-ellipsis size-4 text-n-slate-11" />
        </button>
      </div>
    </div>

    <div class="mt-2 space-y-1.5 text-xs text-n-slate-11">
      <div
        class="flex items-center gap-2"
        :title="`Fechamento previsto: ${formatDate(deal.close_date)}`"
      >
        <span class="i-lucide-calendar size-4 text-n-slate-10" />
        <span>{{ formatDate(deal.close_date) || '-' }}</span>
      </div>
      <div class="flex items-center gap-2" title="Sem atividades">
        <span class="i-lucide-calendar-plus size-4 text-n-slate-10" />
        <span>Sem atividades</span>
      </div>
      <div class="flex items-center gap-2 text-xs text-n-slate-11">
        <span class="i-lucide-funnel size-4 text-n-slate-10" />
        <span class="truncate">{{ getPipelineName }}</span>
      </div>
      <div class="flex gap-1 mt-1">
        <button
          v-for="(stage, index) in stageList"
          :key="`stage-progress-${deal.id}-${stage.id}`"
          class="h-2 flex-1 rounded-full transition-opacity"
          :title="getStageTooltip(stage)"
          :style="getStageProgressStyle(stage, index)"
          @click.stop="setDealStage(stage.id)"
        />
      </div>
    </div>

    <div class="mt-2 flex items-center justify-between">
      <div class="flex items-center gap-2 min-h-9">
        <button
          class="inline-flex h-9 min-w-9 px-1 items-center justify-center rounded-md hover:bg-n-alpha-2"
          title="Responsaveis"
          @click.stop="toggleAssigneeMenu($event)"
        >
          <div v-if="dealAssignees.length" class="flex items-center">
            <span
              v-for="(agent, index) in dealAssignees.slice(0, 3)"
              :key="`${deal.id}-assignee-${agent.id}`"
              class="inline-flex"
              :class="index > 0 ? '-ml-2' : ''"
            >
              <Avatar
                :name="agent.name"
                :src="agent.thumbnail || agent.avatar_url"
                :size="24"
                rounded-full
              />
            </span>
            <span
              v-if="dealAssignees.length > 3"
              class="-ml-2 inline-flex h-6 min-w-6 px-1 items-center justify-center rounded-full bg-n-alpha-2 text-[10px] text-n-slate-12"
            >
              +{{ dealAssignees.length - 3 }}
            </span>
          </div>
          <span
            v-else
            class="inline-flex items-center justify-center size-6 rounded-full bg-n-alpha-2 text-n-slate-11"
            title="Sem responsavel"
          >
            <span class="i-lucide-user size-4" />
          </span>
        </button>
        <button
          class="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
          :title="`Atributos personalizados: ${dealCustomFieldsCount}`"
          @click.stop="openCustomAttributesModal"
        >
          <span class="i-lucide-code size-6" />
          <span
            class="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-n-brand text-white text-[10px] leading-4 text-center"
          >
            {{ dealCustomFieldsCount }}
          </span>
        </button>
        <div class="relative">
          <button
            class="inline-flex h-9 w-9 items-center justify-center rounded-md text-n-slate-12 hover:bg-n-alpha-2"
            :title="`Prioridade: ${priorityMeta.label}`"
            @click.stop="togglePriorityMenu($event)"
          >
            <span
              class="size-6"
              :class="priorityMeta.icon"
              :style="{ color: priorityMeta.color }"
            />
          </button>
        </div>
      </div>
      <span
        class="inline-flex h-9 items-center gap-1 text-xs text-n-slate-11"
        :title="`Criado em: ${formatDate(deal.created_at)}`"
      >
        <span class="i-lucide-clock-3 size-4" />
        {{ formatDealCreatedAt(deal.created_at) }}
      </span>
    </div>

    <div
      class="mt-2 border-t border-n-weak/80 pt-2 flex items-start justify-between gap-2"
    >
      <div
        class="min-w-0 flex-1 flex flex-wrap gap-1.5 max-h-12 overflow-hidden"
      >
        <span
          v-for="labelTitle in visibleLabels"
          :key="`${deal.id}-label-${labelTitle}`"
          class="inline-flex items-center gap-1.5 rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-12"
        >
          <span
            class="inline-block h-2 w-2 shrink-0 rounded-sm"
            :style="{ backgroundColor: getLabelColorByTitle(labelTitle) }"
          />
          <span class="truncate max-w-[120px]">{{ labelTitle }}</span>
        </span>
        <!-- eslint-disable-next-line vue/max-attributes-per-line -->
        <span v-if="!visibleLabels.length" class="text-xs text-n-slate-11 py-1"
          >Sem etiquetas</span
        >
      </div>

      <button
        class="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-n-weak text-n-slate-12"
        :title="`Etiquetas: ${allLabels.length}`"
        @click.stop="toggleLabelsMenu($event)"
      >
        <span class="i-lucide-tags size-5" />
        <span
          v-if="hiddenLabelsCount > 0"
          class="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-n-brand text-white text-[10px] leading-4 text-center"
        >
          +{{ hiddenLabelsCount }}
        </span>
      </button>
    </div>

    <div
      v-if="showOnCardAttributes.length"
      class="mt-2 border-t border-n-weak/80 pt-2 space-y-1"
    >
      <div
        v-for="attr in showOnCardAttributes"
        :key="`card-attr-${deal.id}-${attr.key}`"
        class="flex items-center gap-2 text-xs text-n-slate-11"
      >
        <span class="i-lucide-tag size-3.5 text-n-slate-10 shrink-0" />
        <span class="font-medium text-n-slate-12 shrink-0"
          >{{ attr.label }}:</span
        >
        <span class="truncate">{{ attr.value ?? '—' }}</span>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showActionsMenu"
        class="deal-popover fixed z-[12000] w-56 rounded-lg border border-n-weak bg-n-solid-1 shadow-lg py-1"
        :style="actionsMenuStyle"
        @click.stop
      >
        <button
          class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
          @click.stop="emitEdit"
        >
          <span class="i-lucide-pencil size-4 text-n-slate-11" />Editar negocio
        </button>
        <button
          class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
          @click.stop="openCustomAttributesModal"
        >
          <span class="i-lucide-code size-4 text-n-slate-11" />Atributos
          personalizados
        </button>
        <button
          v-if="isWonOrLost"
          class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
          @click.stop="restoreDealStatus"
        >
          <span class="i-lucide-rotate-ccw size-4 text-n-slate-11" />Restaurar
          status ganho/perdido
        </button>
        <template v-else>
          <button
            class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
            @click.stop="markWon"
          >
            <span class="i-lucide-check size-4 text-n-slate-11" />Ganhar negocio
          </button>
          <button
            class="w-full text-left px-3 py-2 text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2"
            @click.stop="openLoseModal"
          >
            <span class="i-lucide-x size-4 text-n-slate-11" />Perder negocio
          </button>
        </template>
        <button
          class="w-full text-left px-3 py-2 text-sm text-ruby-10 hover:bg-ruby-2 inline-flex items-center gap-2"
          @click.stop="openDeleteModal"
        >
          <span class="i-lucide-trash-2 size-4" />Excluir negocio
        </button>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="showPriorityMenu"
        class="deal-popover fixed z-[12000] w-[230px] max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-xl p-2 space-y-1"
        :style="priorityMenuStyle"
        @click.stop
      >
        <button
          v-for="option in PRIORITY_OPTIONS"
          :key="`priority-${option.value}`"
          class="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-n-alpha-2 inline-flex items-center gap-2 text-n-slate-12"
          @click.stop="setPriority(option.value)"
        >
          <span
            class="size-4"
            :class="option.icon"
            :style="{ color: option.color }"
          />
          {{ option.label }}
        </button>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="showAssigneeMenu"
        class="deal-popover fixed z-[12000] w-[320px] max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-xl p-3 space-y-3"
        :style="assigneeMenuStyle"
        @click.stop
      >
        <p class="text-sm font-medium text-n-slate-12">Responsaveis</p>
        <input
          v-model="assigneeSearch"
          type="text"
          class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-solid-1 text-sm"
          placeholder="Buscar agente..."
        />
        <div
          class="max-h-52 overflow-y-auto border border-n-weak rounded-md py-1"
        >
          <button
            v-for="agent in filteredAgents"
            :key="`agent-${agent.id}`"
            class="w-full px-3 py-2 text-sm text-left text-n-slate-12 hover:bg-n-alpha-2 inline-flex items-center justify-between gap-2"
            @click.stop="toggleAssignee(agent)"
          >
            <span class="inline-flex items-center gap-2 min-w-0">
              <Avatar
                :name="agent.name"
                :src="agent.thumbnail || agent.avatar_url"
                :size="20"
                rounded-full
              />
              <span class="truncate">{{ agent.name }}</span>
            </span>
            <span
              v-if="selectedAssigneeIds.includes(Number(agent.id))"
              class="i-lucide-check size-4 text-n-brand shrink-0"
            />
          </button>
        </div>
        <div class="flex justify-end">
          <button
            class="px-2 py-1 text-xs rounded bg-n-brand text-white"
            @click.stop="saveAssignees"
          >
            Salvar
          </button>
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="showLabelsMenu"
        class="deal-popover fixed z-[12000] w-[300px] max-w-[calc(100vw-2rem)] overflow-auto rounded-lg border border-n-weak bg-n-solid-1 shadow-xl p-3 space-y-3"
        :style="labelsMenuStyle"
        @click.stop
      >
        <input
          v-model="labelSearch"
          type="text"
          class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
          placeholder="Buscar label..."
        />
        <div
          class="max-h-52 overflow-y-auto border border-n-weak rounded-lg divide-y divide-n-weak"
        >
          <button
            v-for="label in filteredLabels"
            :key="`label-${label.title}`"
            class="w-full px-3 py-2 text-sm hover:bg-n-alpha-2 flex items-center justify-between gap-2 text-n-slate-12"
            @click.stop="toggleLabel(label.title)"
          >
            <span class="inline-flex items-center gap-2 min-w-0">
              <span
                class="inline-flex size-2.5 rounded-full shrink-0"
                :style="{ backgroundColor: label.color || '#3B82F6' }"
              />
              <span class="truncate">{{ label.title }}</span>
            </span>
            <span
              v-if="selectedLabels.includes(label.title)"
              class="i-lucide-check size-4 text-green-600 shrink-0"
            />
          </button>
        </div>
        <div class="flex justify-end">
          <button
            class="px-2 py-1 text-xs rounded bg-n-brand text-white"
            @click.stop="saveLabels"
          >
            Salvar
          </button>
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="showProductsMenu"
        class="deal-popover fixed z-[12000] w-[420px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-1.5rem)] flex flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-xl"
        :style="productsMenuStyle"
        @click.stop
      >
        <template v-if="dealProductsView === 'summary'">
          <div class="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
            <div
              v-if="!dealSelectedProducts.length"
              class="text-center py-6 text-sm text-n-slate-12"
            >
              Nenhum produto adicionado.
            </div>

            <div v-else class="max-h-56 overflow-y-auto space-y-2 pr-1">
              <div
                v-for="item in dealSelectedProducts"
                :key="`deal-product-${item.product_id}`"
                class="border-b border-n-weak pb-2 last:border-0 last:pb-0"
              >
                <div class="flex items-center justify-between gap-2">
                  <p class="text-sm text-n-slate-12 truncate">
                    {{ item.name }}
                  </p>
                  <span class="text-sm font-medium text-n-slate-12">{{
                    formatCurrency(item.unit_price)
                  }}</span>
                </div>
                <div class="flex items-center justify-between gap-2 mt-1">
                  <p class="text-xs text-n-slate-11 truncate">
                    {{ item.sku || '-' }}
                  </p>
                  <div class="flex items-center border border-n-weak rounded">
                    <button
                      class="px-2 py-0.5 text-n-slate-12"
                      @click.stop="decreaseDealQty(item)"
                    >
                      -
                    </button>
                    <span class="px-2 text-sm text-n-slate-12">{{
                      item.quantity
                    }}</span>
                    <button
                      class="px-2 py-0.5 text-n-slate-12"
                      @click.stop="increaseDealQty(item)"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="text-right">
              <button
                class="text-sm text-n-brand"
                @click.stop="dealProductsView = 'catalog'"
              >
                Adicionar produto
              </button>
            </div>

            <div
              class="border-t border-n-weak pt-2 space-y-1.5 text-sm text-n-slate-12"
            >
              <div class="flex justify-between">
                <span>Total de produtos</span>
                <span class="font-medium">{{
                  formatCurrency(dealSubtotalProducts)
                }}</span>
              </div>
              <div class="flex items-center justify-between gap-2">
                <span>Cupom</span>
                <input
                  v-model="dealCouponCode"
                  type="text"
                  class="w-40 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                  placeholder="Código do cupom"
                  @blur="applyDealCoupon"
                />
              </div>
              <div class="flex items-center justify-between gap-2">
                <span>Desconto (-)</span>
                <input
                  v-model="dealManualDiscount"
                  type="number"
                  min="0"
                  step="0.01"
                  class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                />
              </div>
              <div class="flex items-center justify-between gap-2">
                <span>Acrescimo (+)</span>
                <input
                  v-model="dealSurcharge"
                  type="number"
                  min="0"
                  step="0.01"
                  class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                />
              </div>
              <div class="flex items-center justify-between gap-2">
                <span>Frete (+)</span>
                <input
                  v-model="dealShipping"
                  type="number"
                  min="0"
                  step="0.01"
                  class="w-28 px-2 py-1 border rounded border-n-weak bg-n-alpha-2 text-xs text-right text-n-slate-12"
                />
              </div>
            </div>
          </div>

          <div
            class="shrink-0 border-t border-n-weak bg-n-solid-1 p-3 space-y-2"
          >
            <div
              class="flex items-center justify-between text-base font-semibold text-n-slate-12"
            >
              <span>Total</span>
              <span>{{ formatCurrency(dealFinalAmount) }}</span>
            </div>

            <div class="flex justify-end gap-2">
              <button
                class="px-2 py-1 text-xs rounded border border-n-weak"
                @click.stop="showProductsMenu = false"
              >
                Cancelar
              </button>
              <button
                class="px-2 py-1 text-xs rounded bg-n-brand text-white"
                :disabled="dealPopoverSaving"
                @click.stop="saveDealProducts"
              >
                {{ dealPopoverSaving ? 'Salvando...' : 'Salvar' }}
              </button>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="flex-1 overflow-y-auto p-3 space-y-3">
            <input
              v-model="dealProductSearch"
              type="text"
              class="w-full px-3 py-2 border rounded-lg border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
              placeholder="Buscar produto..."
            />

            <div
              class="max-h-56 overflow-y-auto border border-n-weak rounded-lg py-1"
            >
              <button
                v-for="product in filteredDealCatalogProducts"
                :key="`deal-catalog-${product.id}`"
                class="w-full text-left px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-2"
                @click.stop="addProductToDealPopover(product)"
              >
                {{ product.name }}
              </button>
              <p
                v-if="!filteredDealCatalogProducts.length"
                class="px-3 py-2 text-sm text-n-slate-12"
              >
                Nenhum produto encontrado
              </p>
            </div>

            <div class="flex items-center justify-between">
              <button
                class="text-sm text-n-slate-12"
                @click.stop="dealProductsView = 'summary'"
              >
                Voltar
              </button>
              <button
                class="text-sm text-n-brand"
                @click.stop="
                  showCreateDealProductForm = !showCreateDealProductForm
                "
              >
                Criar
              </button>
            </div>

            <div
              v-if="showCreateDealProductForm"
              class="space-y-2 border border-n-weak rounded-lg p-2"
            >
              <input
                v-model="dealNewProduct.name"
                type="text"
                class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
                placeholder="Nome do produto"
              />
              <input
                v-model="dealNewProduct.sku"
                type="text"
                class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
                placeholder="SKU"
              />
              <input
                v-model="dealNewProduct.price"
                type="number"
                min="0"
                step="0.01"
                class="w-full px-2 py-1.5 border rounded border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
                placeholder="Preco"
              />
              <div class="flex justify-end gap-2">
                <button
                  class="px-2 py-1 text-xs rounded border border-n-weak"
                  @click.stop="showCreateDealProductForm = false"
                >
                  Cancelar
                </button>
                <button
                  class="px-2 py-1 text-xs rounded bg-n-brand text-white"
                  @click.stop="createDealProductQuickly"
                >
                  Salvar produto
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </teleport>

    <!-- Modals inside the card div to ensure single root element (required for vuedraggable) -->
    <woot-modal v-model:show="showLoseDealModal" :on-close="closeLoseModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        header-title="Perder negocio"
        header-content="Selecione o motivo da perda para atualizar o negocio."
      />
      <div class="modal-content w-full !pt-6 space-y-4">
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-slate-12 block"
            >Motivo</label
          >
          <select
            v-model="selectedLossReasonId"
            class="w-full px-3 py-2 border rounded border-n-weak bg-n-solid-1 text-sm"
          >
            <option :value="null">Selecione</option>
            <option
              v-for="reason in lossReasons"
              :key="`loss-${reason.id}`"
              :value="reason.id"
            >
              {{ reason.name }}
            </option>
          </select>
        </div>
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-slate-12 block"
            >Justificativa</label
          >
          <textarea
            v-model="lossJustification"
            rows="3"
            class="w-full px-3 py-2 border rounded border-n-weak bg-n-solid-1 text-sm"
            placeholder="Opcional"
          />
        </div>
        <div class="flex justify-end gap-2">
          <button
            class="px-3 py-1.5 rounded border border-n-weak text-sm"
            @click="closeLoseModal"
          >
            Cancelar
          </button>
          <button
            class="px-3 py-1.5 rounded bg-ruby-9 text-white text-sm"
            @click="confirmLoseDeal"
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  </woot-modal>

  <Dialog
    ref="confirmDeleteModal"
    type="alert"
    title="Excluir negócio"
    description="Tem certeza que deseja excluir este negócio? Esta ação não pode ser desfeita."
    confirm-button-label="Excluir"
    @confirm="removeDeal"
  />

  <woot-modal
    v-model:show="showCustomAttributesModal"
    :on-close="closeCustomAttributesModal"
  >
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        header-title="Atributos do negocio"
        header-content="Visualize e gerencie os atributos personalizados deste negocio."
      />
      <div class="modal-content w-full !pt-6 space-y-3">
        <div
          class="space-y-2 max-h-[40vh] overflow-auto border border-n-weak rounded p-3"
        >
          <div
            v-for="entry in dealCustomFieldsEntries"
            :key="`deal-custom-field-${deal.id}-${entry.key}`"
            class="flex items-start justify-between gap-3 text-sm"
          >
            <span class="font-medium text-n-slate-12">{{ entry.label }}</span>
            <span class="text-n-slate-11 text-right break-all">{{
              String(entry.value)
            }}</span>
          </div>
          <p
            v-if="!dealCustomFieldsEntries.length"
            class="text-sm text-n-slate-11"
          >
            Nenhum atributo personalizado preenchido.
          </p>
        </div>
        <div class="flex justify-between gap-2">
          <button
            class="px-3 py-1.5 rounded border border-n-weak text-sm"
            @click="openCustomAttributesManagementModal"
          >
            Gerenciar atributos
          </button>
          <button
            class="px-3 py-1.5 rounded border border-n-weak text-sm"
            @click="openAddDealAttributeModal"
          >
            Criar atributo personalizado
          </button>
        </div>
        <div class="flex justify-end gap-2">
          <button
            class="px-3 py-1.5 rounded border border-n-weak text-sm"
            @click="closeCustomAttributesModal"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </woot-modal>

  <woot-modal
    v-model:show="showCustomAttributesManageModal"
    :on-close="closeCustomAttributesManagementModal"
  >
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        header-title="Gerenciar atributos"
        header-content="Adicione e atualize os atributos personalizados deste negocio."
      />
      <div class="modal-content w-full !pt-8 flex min-h-0 flex-col gap-4">
        <div class="space-y-2">
          <label class="text-sm font-medium text-n-slate-12 block"
            >Atributo</label
          >
          <ComboBox
            v-model="selectedCustomAttributeKey"
            :options="dealAttributeDefinitionOptions"
            placeholder="Selecione um atributo"
            use-fixed-dropdown
            class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
          />
          <div class="flex justify-end">
            <button
              class="px-3 py-1.5 rounded border border-n-weak text-sm"
              @click="addCustomFieldToDraft"
            >
              Adicionar
            </button>
          </div>
        </div>

        <div class="flex-1 min-h-0 space-y-3 overflow-y-auto pr-1">
          <div
            v-for="key in dealCustomFieldKeys"
            :key="`deal-attr-key-${deal.id}-${key}`"
            class="rounded-lg border border-n-weak p-3 space-y-2.5"
          >
            <div class="flex items-center justify-between gap-2">
              <label class="text-sm font-medium text-n-slate-12">
                {{ getDraftCustomFieldLabel(key) }}
              </label>
              <button
                class="text-xs text-ruby-10 hover:underline"
                @click="removeCustomFieldFromDraft(key)"
              >
                Remover
              </button>
            </div>

            <input
              v-if="
                ['text', 'link', 'phone_number'].includes(
                  getDraftCustomFieldType(key)
                )
              "
              v-model="customAttributesDraft[key]"
              type="text"
              class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
            />

            <input
              v-else-if="
                ['number', 'currency'].includes(getDraftCustomFieldType(key))
              "
              :value="customAttributesDraft[key] ?? ''"
              type="number"
              class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
              @input="
                customAttributesDraft[key] =
                  $event.target.value === '' ? '' : Number($event.target.value)
              "
            />

            <input
              v-else-if="getDraftCustomFieldType(key) === 'date'"
              v-model="customAttributesDraft[key]"
              type="date"
              class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
            />

            <ComboBox
              v-else-if="
                isSelectField({
                  attribute_display_type: getDraftCustomFieldType(key),
                })
              "
              :model-value="getDraftCustomFieldValue(key)"
              :options="getDraftCustomFieldComboOptions(key)"
              placeholder="Selecione"
              use-fixed-dropdown
              class="[&>div>button]:h-10 [&>div>button]:!bg-n-solid-1"
              @update:model-value="customAttributesDraft[key] = $event"
            />

            <label
              v-else-if="getDraftCustomFieldType(key) === 'checkbox'"
              class="inline-flex items-center gap-2 text-sm text-n-slate-12"
            >
              <input
                :checked="Boolean(customAttributesDraft[key])"
                type="checkbox"
                @change="customAttributesDraft[key] = $event.target.checked"
              />
              Ativo
            </label>

            <input
              v-else
              v-model="customAttributesDraft[key]"
              type="text"
              class="w-full h-10 px-3 border rounded-md border-n-weak bg-n-alpha-2 text-sm"
            />
          </div>

          <p
            v-if="!dealCustomFieldKeys.length"
            class="text-sm text-center text-n-slate-11 py-6"
          >
            Nenhum atributo adicionado.
          </p>
        </div>

        <div class="shrink-0 flex items-center justify-between pt-1">
          <button
            class="text-sm text-n-brand"
            @click="openAddDealAttributeModal"
          >
            Criar atributo personalizado
          </button>
          <div class="flex gap-2">
            <button
              class="px-3 py-1.5 rounded border border-n-weak text-sm"
              @click="closeCustomAttributesManagementModal"
            >
              Cancelar
            </button>
            <button
              class="px-3 py-1.5 rounded bg-n-brand text-white text-sm"
              @click="saveCustomAttributes"
            >
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  </woot-modal>

    <AddAttribute
      v-if="showAddDealAttributeModal"
      :on-close="closeAddDealAttributeModal"
      :selected-attribute-model-tab="2"
      hide-attribute-model-selector
    />
  </div>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
